import { cx } from "@jugl-web/utils";
import { forwardRef, ReactNode } from "react";
import {
  InteractiveContainer,
  InteractiveContainerProps,
} from "../../../primitives/InteractiveContainer";
import { CellCopy } from "./components/CellCopy";

export interface TableCellProps {
  children: ReactNode;
  htmlTitle?: string;
  tabIndex?: number;
  classNames?: {
    cell?: string;
    content?: string;
  };
  clipboardContent?: string;
  clipboardSuccessMessage?: string;
  startContent?: ReactNode;
  endContent?: ReactNode;
  /**
   * Whether the cell is disabled and any interactivity is cancelled
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Whether the cell should show hover styles (background and outline)
   * Note: cell has to be interactive to apply hover styles
   * @default true
   */
  isHoverable?: boolean;
  /**
   * Whether the cell is in a focused/selected state
   * @default false
   */
  isFocused?: boolean;
  onClick?: InteractiveContainerProps["onClick"];
}

/**
 * TableCell component for rendering content within table cells.
 * Supports interactive states like hover and focus with visual feedback.
 */
export const TableCell = forwardRef<HTMLDivElement, TableCellProps>(
  (
    {
      children,
      htmlTitle,
      tabIndex,
      classNames,
      startContent,
      endContent,
      clipboardContent,
      clipboardSuccessMessage,
      isDisabled = false,
      isHoverable = true,
      isFocused = false,
      onClick,
    },
    ref
  ) => {
    const isInteractive = !!onClick && !isDisabled;
    const canHover = isInteractive && isHoverable;

    return (
      <InteractiveContainer
        ref={ref}
        isTextSelectable
        id="table-grid-cell"
        className={cx(
          "group/cell relative flex h-full min-h-[60px] w-full items-center gap-2 px-4 font-secondary text-sm text-gray-800 transition-colors",
          "outline outline-0 outline-offset-[-1px] outline-grey-400",
          "focus-visible:outline-2 focus-visible:outline-primary-200 focus-visible:hover:outline-2",
          isInteractive ? "cursor-pointer" : "cursor-default",
          canHover && "hover:bg-grey-100 hover:outline-1",
          isFocused &&
            "bg-grey-100 outline-2 outline-primary-200 hover:outline-2",
          classNames?.cell
        )}
        title={htmlTitle}
        tabIndex={tabIndex ?? (!isInteractive ? -1 : undefined)}
        isDisabled={isDisabled}
        onClick={onClick}
      >
        {startContent && (
          <div className="flex shrink-0 items-center">{startContent}</div>
        )}
        <div
          id="table-grid-cell-content"
          className={cx("flex-1 truncate", classNames?.content)}
        >
          {children}
        </div>
        {clipboardContent && (
          <CellCopy
            copyContent={clipboardContent}
            successMessage={clipboardSuccessMessage}
          />
        )}
        {endContent && (
          <div className="flex shrink-0 items-center">{endContent}</div>
        )}
      </InteractiveContainer>
    );
  }
);

TableCell.displayName = "TableCell";
